import {DocumentIcon, DocumentTextIcon, ReceiptPercentIcon} from '@heroicons/react/24/outline';
import {
    createTable,
    TableHeaderSpec
} from '../../../../components/table/CreateTable';

import {DOCUMENT_DIRECTION_LABELS} from "../../../../types/enums.ts";
import {RODeveloperDocument} from "../types.ts";
import {Tag} from "../../../../components/general/Tag.tsx";
import {cleanDocumentFileName} from "../../../../lib/TFICLib.ts";


interface RODeveloperDocumentsTableProps {
    documents: RODeveloperDocument[];
    onRowClick: (doc: RODeveloperDocument) => void;
}

export const RODeveloperDocumentsTable = ({documents, onRowClick}: RODeveloperDocumentsTableProps) => {

    const getDocumentIcon = (category: string) => {
        const categoryLower = (category || "").toLowerCase();
        if (categoryLower === 'invoice') {
            return DocumentTextIcon;
        } else if (categoryLower === 'pos_receipt') {
            return ReceiptPercentIcon;
        } else {
            return DocumentIcon;
        }
    };

    const getStatusVariant = (status: string) => {
        const s = (status || "").toLowerCase();
        switch (s) {
            case 'processed': return 'success';
            case 'error': return 'error';
            case 'running': return 'primary';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const tableSpec: TableHeaderSpec<RODeveloperDocument> = {
        title: `${documents.length} Documents`,
        columns: [
            {
                name: 'Document',
                field: 'file_name',
                style: { widthMultiplier: 3 },
                customRender: (doc) => {
                    const IconComponent = getDocumentIcon(doc.document_category);
                    return (
                        <div className="flex items-center max-w-full">
                            <IconComponent className="h-5 w-5 text-gray-400 mr-3 shrink-0" />
                            <span className="text-sm font-medium text-gray-900 truncate" title={doc.file_name}>
                                {cleanDocumentFileName(doc.file_name)}
                            </span>
                        </div>
                    );
                }
            },
            {
                name: 'Category',
                field: 'document_category',
                style: { widthMultiplier: 1 },
                customRender: (doc) => (
                    <span className="capitalize">{doc.document_category?.replace('_', ' ')}</span>
                )
            },
            {
                name: 'Direction',
                field: 'document_direction',
                customRender: (doc) => DOCUMENT_DIRECTION_LABELS[doc.document_direction] || doc.document_direction,
                style: { widthMultiplier: 1.5 }
            },
            {
                name: 'Status',
                field: 'status',
                style: { widthMultiplier: 1 },
                customRender: (doc) => (
                    <Tag variant={getStatusVariant(doc.status)} size="xs" className="capitalize">
                        {doc.status}
                    </Tag>
                )
            },
            {
                name: 'Review',
                field: 'needs_review',
                style: { widthMultiplier: 1 },
                customRender: (doc) => (
                    doc.needs_review ? (
                        <Tag variant="error" size="xs">Needs Review</Tag>
                    ) : (
                        <span className="text-gray-400 text-xs">No</span>
                    )
                )
            }
        ],
        onRowClick: (row) => onRowClick(row),
        options: {
            allowFullRowClick: true
        }
    };

    const TableComponent = createTable<RODeveloperDocument>(documents, tableSpec);

    return TableComponent();
};
